import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Star, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CountrySelect, StateCombobox } from "@/components/shared/CountryStateFields";
import {
  isValidPostalCode, isValidUsPhone, digitsOnly, sanitizePostalCode, postalCodeMaxLen,
  PHONE_ERROR, ZIP_ERROR, PHONE_MAX_DIGITS,
} from "../../utils/validation";
import { useInfinityCustomer } from "../../context/InfinityCustomerContext";

const empty = { firstname: "", lastname: "", address: "", city: "", state: "", zipcode: "", country: "US", phone: "" };

/**
 * Manage Address — the family member's saved address book. Stored against their
 * customer record so it follows them to another device; checkout prefills the
 * default.
 */
export default function InfinityAddresses() {
  const { isAuthed, addresses, saveAddress, deleteAddress, setDefaultAddress, basePath, theme } = useInfinityCustomer();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [adding, setAdding] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!isAuthed) navigate(`${basePath}/login`); /* eslint-disable-next-line */ }, []);

  // Previously this returned silently on bad input, so "Save address" looked
  // like it did nothing. Every failure now names the field that needs fixing.
  const submit = async () => {
    const e = {};
    ["address", "city", "state", "zipcode"].forEach((k) => { if (!form[k]) e[k] = "Required"; });
    if (form.zipcode && !isValidPostalCode(form.zipcode, form.country)) {
      e.zipcode = form.country === "US" ? ZIP_ERROR : "Enter a valid postal code";
    }
    // Phone is optional on a saved address, but must be well-formed if given.
    if (form.phone && !isValidUsPhone(form.phone)) e.phone = PHONE_ERROR;
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setSaving(true);
    try {
      await saveAddress(form);
      setForm(empty);
      setErrors({});
      setAdding(false);
    } catch {
      toast.error("Could not save the address. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const run = async (fn, message) => {
    try { await fn(); } catch { toast.error(message); }
  };

  /*
   * `sanitize` runs on every keystroke so ZIP and phone cannot hold a letter or
   * run past their digit count — the same as-you-type behaviour as the engraving
   * box. The submit-time checks above stay as the backstop for empty fields.
   */
  const fld = (label, key, { maxLength, sanitize, inputMode } = {}) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input
        className={errors[key] ? "border-red-500" : undefined}
        value={form[key]}
        maxLength={maxLength}
        inputMode={inputMode}
        onChange={(e) => setForm({ ...form, [key]: sanitize ? sanitize(e.target.value) : e.target.value })}
      />
      {errors[key] ? <p className="text-xs text-red-600">{errors[key]}</p> : null}
    </div>
  );

  const picker = (label, key, node) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {node}
      {errors[key] ? <p className="text-xs text-red-600">{errors[key]}</p> : null}
    </div>
  );

  return (
    <div className="max-w-2xl space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900">Manage Address</h2>
        {!adding ? <Button onClick={() => setAdding(true)}><Plus className="w-4 h-4 mr-1" /> Add address</Button> : null}
      </div>

      {addresses.length === 0 && !adding ? (
        <p className="text-slate-400">No saved addresses yet.</p>
      ) : null}

      <div className="space-y-3">
        {addresses.map((a) => (
          <div key={a.id} className="card-elevated p-4 flex items-start justify-between">
            <div className="text-sm">
              <p className="font-medium text-slate-800">{a.firstname} {a.lastname} {a.is_default ? <span className="text-xs text-green-600">(default)</span> : null}</p>
              <p className="text-slate-600">{a.address}, {a.city}, {a.state} {a.zipcode}, {a.country}</p>
              {a.phone ? <p className="text-slate-500">{a.phone}</p> : null}
            </div>
            <div className="flex items-center gap-2">
              {!a.is_default ? <button onClick={() => run(() => setDefaultAddress(a.id), "Could not update the default address.")} title="Set default" className="text-slate-400 hover:text-amber-500"><Star className="w-4 h-4" /></button> : null}
              <button onClick={() => run(() => deleteAddress(a.id), "Could not delete the address.")} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {adding ? (
        <div className="card-elevated p-5 space-y-3">
          <div className="grid grid-cols-2 gap-2">{fld("First name", "firstname")}{fld("Last name", "lastname")}</div>
          {fld("Address", "address")}
          <div className="grid grid-cols-2 gap-2">
            {fld("City", "city")}
            {picker("State", "state", (
              <StateCombobox
                country={form.country}
                value={form.state}
                onChange={(v) => setForm({ ...form, state: v })}
                className="h-10"
                invalid={!!errors.state}
              />
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {fld("ZIP", "zipcode", {
              maxLength: postalCodeMaxLen(form.country),
              inputMode: form.country === "US" ? "numeric" : "text",
              sanitize: (v) => sanitizePostalCode(v, form.country),
            })}
            {picker("Country", "country", (
              <CountrySelect
                value={form.country}
                /* Clearing the state is required, not tidiness: a US state left
                   behind on a Canadian address would ship to nowhere. */
                onChange={(v) => setForm({ ...form, country: v, state: "" })}
                className="h-10"
              />
            ))}
          </div>
          {fld("Phone", "phone", { maxLength: PHONE_MAX_DIGITS, inputMode: "tel", sanitize: (v) => digitsOnly(v, PHONE_MAX_DIGITS) })}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setAdding(false); setForm(empty); setErrors({}); }}>Cancel</Button>
            <Button style={{ backgroundColor: theme.accent_color || "#F2B705", color: "#1f2937" }} onClick={submit} disabled={saving}>
              {saving ? "Saving…" : "Save address"}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
